"use client";

import { cartCanShip, shippingSubtotal } from "../cart-line-utils";
import {
  type ShopPromotionAnnouncement,
  getShopPromotionAnnouncements,
  getShopShippingAnnouncement,
} from "../promotion-messaging";

import React, { useCallback, useEffect, useState } from "react";

import {
  AnnouncementBar,
  announcementBarTextClassName,
} from "@/components/announcement-bar";

import { useShopCatalog } from "@/contexts/shop-catalog-provider";

import { cx } from "@/lib/tailwind-utils";

import type { ShopCartLine } from "@/types/shop";

const AUTO_ADVANCE_MS = 4_750;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function usePromotionAnnouncementCarousel(announcementCount: number) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = () => setReduceMotion(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    setActiveIndex((current) =>
      announcementCount > 0 ? Math.min(current, announcementCount - 1) : 0,
    );
  }, [announcementCount]);

  const showNext = useCallback(() => {
    setActiveIndex((current) =>
      announcementCount > 0 ? (current + 1) % announcementCount : 0,
    );
  }, [announcementCount]);

  const isPaused = hoverPaused || focusPaused || reduceMotion;
  useEffect(() => {
    if (announcementCount < 2 || isPaused) return;
    const interval = window.setInterval(showNext, AUTO_ADVANCE_MS);
    return () => window.clearInterval(interval);
  }, [announcementCount, isPaused, showNext]);

  return {
    activeIndex,
    reduceMotion,
    setHoverPaused,
    setFocusPaused,
  };
}

export const PromotionAnnouncementCarousel: React.FC<{
  announcements: ShopPromotionAnnouncement[];
  activePromotionId: string | null;
  onSelectPromotion: (promotionId: string) => void;
}> = ({ announcements, activePromotionId, onSelectPromotion }) => {
  const { activeIndex, reduceMotion, setHoverPaused, setFocusPaused } =
    usePromotionAnnouncementCarousel(announcements.length);

  if (announcements.length === 0) return null;

  const activeAnnouncement = announcements[activeIndex] ?? announcements[0];
  const hasMultiple = announcements.length > 1;

  return (
    <AnnouncementBar
      role="region"
      aria-roledescription="carousel"
      aria-label="Promotion announcements"
      tabIndex={hasMultiple ? 0 : undefined}
      className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-foreground"
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onFocus={() => setFocusPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocusPaused(false);
        }
      }}
    >
      {activeAnnouncement.promotionId ? (
        <button
          key={activeAnnouncement.id}
          type="button"
          data-testid="promotion-announcement"
          className={cx(
            announcementBarTextClassName,
            "rounded-sm hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground",
            !reduceMotion && "motion-preset-fade-sm motion-duration-200",
          )}
          aria-pressed={activePromotionId === activeAnnouncement.promotionId}
          onClick={() => onSelectPromotion(activeAnnouncement.promotionId!)}
        >
          {hasMultiple && (
            <span className="sr-only">
              Promotion {activeIndex + 1} of {announcements.length}:{" "}
            </span>
          )}
          {activeAnnouncement.text}
        </button>
      ) : (
        <span
          key={activeAnnouncement.id}
          data-testid="promotion-announcement"
          className={cx(
            announcementBarTextClassName,
            !reduceMotion && "motion-preset-fade-sm motion-duration-200",
          )}
        >
          {hasMultiple && (
            <span className="sr-only">
              Promotion {activeIndex + 1} of {announcements.length}:{" "}
            </span>
          )}
          {activeAnnouncement.text}
        </span>
      )}
    </AnnouncementBar>
  );
};

export const ShopPromotionAnnouncementBar: React.FC<{
  lines: ShopCartLine[];
  /** The patient chose pickup, so a free-shipping countdown no longer applies. */
  isPickup?: boolean;
}> = ({ lines, isPickup = false }) => {
  const { catalog, promotionFocus } = useShopCatalog();
  const shippingAnnouncement =
    !isPickup &&
    (lines.length === 0 || cartCanShip(lines, catalog.items, catalog.shipping))
      ? getShopShippingAnnouncement(
          catalog.shipping,
          shippingSubtotal(lines, catalog.items),
        )
      : null;
  const announcements = [
    ...(shippingAnnouncement ? [shippingAnnouncement] : []),
    ...getShopPromotionAnnouncements(catalog.promotions),
  ];
  return (
    <PromotionAnnouncementCarousel
      announcements={announcements}
      activePromotionId={promotionFocus?.state.focusedPromotionId ?? null}
      onSelectPromotion={
        promotionFocus?.actions.focusPromotion ?? (() => undefined)
      }
    />
  );
};
